/**
 * UI module - Alerts, notifications, and global UI updates
 */
import { createAlertElement, createCameraErrorAlert } from '../utils/sanitize.js';
import { log } from '../utils/log.js';
import { ALERT_DURATION } from '../constants.js';

/**
 * DOM element references
 */
let alertsContainer = null;

/**
 * Initializes the UI module with DOM references
 * @param {object} elements - DOM element references
 */
export function initUI(elements) {
  alertsContainer = elements.alertsContainer || document.getElementById('alerts-container');
}

/**
 * Shows an alert message
 * @param {string} message - Alert message
 * @param {string} variant - Alert variant (danger, success, warning, info)
 */
export function showAlert(message, variant = 'danger') {
  const container = alertsContainer || document.getElementById('alerts-container');
  if (!container) return;

  const alertEl = createAlertElement(message, variant);
  container.appendChild(alertEl);

  // Auto-dismiss handler
  alertEl.addEventListener('alert-close', () => {
    alertEl.remove();
  });

  // Auto-remove after timeout
  setTimeout(() => {
    if (alertEl.parentNode) {
      alertEl.remove();
    }
  }, ALERT_DURATION);
}

/**
 * Updates the total for a single card
 * @param {HTMLElement} card - Card element
 */
export function updateCardTotal(card) {
  const prixNetEl = card.querySelector('.prix-net');
  const quantityInput = card.querySelector('.quantity-input');
  const totalElement = card.querySelector('.total-card');

  if (!prixNetEl || !quantityInput || !totalElement) return;

  const prixNet = parseFloat(prixNetEl.textContent) || 0;
  const quantity = parseInt(quantityInput.value, 10) || 1;
  const newTotal = (prixNet * quantity).toFixed(2);

  totalElement.textContent = newTotal;

  log.info(`Updated card total for product ID: ${card.dataset?.productId} to ${newTotal}`);
  updateGlobalTotal();
}

/**
 * Updates the global total across all cards
 */
export function updateGlobalTotal() {
  const container = document.getElementById('scanned-items-container');
  if (!container) return;

  const cards = container.querySelectorAll('.card');
  let globalTotal = 0;

  cards.forEach(card => {
    const totalEl = card.querySelector('.total-card');
    if (totalEl) {
      globalTotal += parseFloat(totalEl.textContent) || 0;
    }
  });

  const newGlobalTotal = globalTotal.toFixed(2);
  const globalTotalEl = document.querySelector('#global-total span');

  if (globalTotalEl) {
    globalTotalEl.textContent = newGlobalTotal;
  }

  log.info(`Updated global total to: ${newGlobalTotal}`);
}

/**
 * Displays camera error in the camera panel
 * @param {HTMLElement} cameraPanel - Camera panel element
 * @param {Error} error - Error object
 */
export function displayCameraError(cameraPanel, error) {
  if (!cameraPanel) return;

  let errorMessage;

  if (error.name === 'NotAllowedError') {
    errorMessage =
      '<strong>Error accessing camera</strong><br>Permission to use webcam was denied or video Autoplay is disabled. Reload the page to give appropriate permissions to webcam.';
  } else {
    errorMessage = error.message || 'Unknown camera error';
  }

  const alertEl = createCameraErrorAlert(errorMessage);

  // Clear panel and add error
  cameraPanel.innerHTML = '';
  cameraPanel.appendChild(alertEl);
}

/**
 * Shows/hides scan-related UI elements
 * @param {object} elements - UI elements
 * @param {boolean} scanning - Whether scanning is active
 */
export function toggleScanUI(elements, scanning) {
  const { scanBtn, scanFrameEl, videoCaptureActionsEl } = elements;

  if (scanning) {
    scanBtn?.setAttribute('hidden', '');
    scanFrameEl?.removeAttribute('hidden');
    videoCaptureActionsEl?.removeAttribute('hidden');
  } else {
    scanBtn?.removeAttribute('hidden');
    scanFrameEl?.setAttribute('hidden', '');
    videoCaptureActionsEl?.setAttribute('hidden', '');
  }
}

/**
 * Updates dossier display elements
 * @param {string} dossierId - Dossier ID to display
 * @param {object} elements - Display elements
 */
export function updateDossierDisplay(dossierId, elements) {
  const { dossierDisplay, dossierInputContainer, dossierDisplayContainer } = elements;

  if (dossierDisplay) {
    dossierDisplay.textContent = `Numéro dossier : ${dossierId}`;
  }

  if (dossierInputContainer) {
    dossierInputContainer.hidden = true;
  }

  if (dossierDisplayContainer) {
    dossierDisplayContainer.hidden = false;
  }
}

/**
 * Clears the scanned items container
 */
export function clearScannedItems() {
  const container = document.getElementById('scanned-items-container');
  if (container) {
    container.innerHTML = '';
  }
  updateGlobalTotal();
}
